import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Button, Icon, Input, Table, TitleSecondary } from '../../components';
import { TableCell, TableRow } from '../../components/tableSimple/tableSimple';

import { actions } from '../../state';
import { centsToMoney, formatDate, mapCouponStatus } from '../../utils';
import { CouponTypes, isDiscountCouponType } from '../coupons/coupon.data';

import styles from './clients.module.scss';

const ClientDiscounts = ({
  dispatch,
  client_discounts,
  onAssignDiscountToClient,
  discounts: { discounts, loading },
}) => {
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [discount, setDiscount] = useState('');

  const fetchDiscounts = useCallback(() => {
    dispatch(
      actions.discounts.getDiscountsAction({
        limit: 1000,
        offset: 0,
        order: [
          {
            by: 'code',
            order: 'asc',
          },
        ],
      })
    );
  }, [dispatch]);

  const clearDiscounts = useCallback(() => {
    dispatch(actions.discounts.clearDiscountsAction());
  }, [dispatch]);

  useEffect(() => () => clearDiscounts(), [clearDiscounts]);

  const discountOptions = useMemo(
    () => [
      { name: 'Pasirinkite nuolaidą', value: '' },
      ...discounts.map(discount => ({
        name: discount.code,
        value: discount.id,
      })),
    ],
    [discounts]
  );

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  const sortedDiscounts = useMemo(
    () =>
      [...client_discounts].sort((a, b) => {
        const first = new Date(
          b.valid_from || b.created_at || b.valid_until || 0
        ).getTime();
        const second = new Date(
          a.valid_from || a.created_at || a.valid_until || 0
        ).getTime();
        return first - second;
      }),
    [client_discounts]
  );

  const visibleDiscounts = showAllHistory
    ? sortedDiscounts
    : sortedDiscounts.slice(0, 3);

  const hiddenDiscountsCount = sortedDiscounts.length - visibleDiscounts.length;

  const tableHeader = [
    '#',
    'Nuolaida',
    'Statusas',
    'Galioja nuo - iki',
    <div className={styles.tableHeaderRight}>% / €</div>,
  ];

  const formatDiscountAmount = (type, amount, membershipId) => {
    const normalizedType = Number(type);

    if (
      normalizedType === CouponTypes.MEMBERSHIP ||
      membershipId !== undefined
    ) {
      return '-';
    }

    if (normalizedType === CouponTypes.VIRTUAL) {
      const amountInEur =
        amount !== undefined && amount !== null ? centsToMoney(amount) : 0;
      return `${amountInEur}€`;
    }
    if (isDiscountCouponType(normalizedType)) {
      return `${amount}%`;
    }

    return `${amount}`;
  };

  const renderTable = () =>
    visibleDiscounts.map(
      ({
        id,
        title_lt,
        valid_from,
        valid_until,
        amount,
        status,
        type,
        membership_id,
      }) => {
        return (
          <TableRow key={id}>
            <TableCell>{id}</TableCell>
            <TableCell>{title_lt}</TableCell>
            <TableCell>{mapCouponStatus(status)}</TableCell>
            <TableCell>
              {formatDate(valid_from)}-{formatDate(valid_until)}
            </TableCell>
            <TableCell align="right">
              {formatDiscountAmount(type, amount, membership_id)}
            </TableCell>
          </TableRow>
        );
      }
    );

  return (
    <div className={styles.discountPlansContainer}>
      <TitleSecondary>Nuolaidos</TitleSecondary>
      <div className={styles.activateDiscountContainer}>
        <Input
          name="code"
          type="string"
          value={discount}
          onChange={e => setDiscount(e.target.value)}
        />
        <Button
          color="primary"
          onClick={() => onAssignDiscountToClient({ code: discount })}
        >
          Aktyvuoti nuolaidą
        </Button>
      </div>
      <div className={styles.discountPlansHistoryContainer}>
        <h4 className={styles.discountPlansHistoryTitle}>Istorija</h4>
        <Table
          header={tableHeader}
          isEmpty={visibleDiscounts.length === 0}
          filter={false}
          loading={loading}
          footer={null}
        >
          {renderTable()}
        </Table>
        {sortedDiscounts.length > 3 && (
          <button
            type="button"
            className={styles.showMoreButton}
            onClick={() => setShowAllHistory(prev => !prev)}
          >
            {showAllHistory
              ? 'Rodyti mažiau'
              : `Rodyti daugiau (${hiddenDiscountsCount})`}
            <Icon
              name="chevron-down"
              classes={{
                icon: `${styles.showMoreButtonIcon} ${
                  showAllHistory ? styles.showMoreButtonIconExpanded : ''
                }`,
              }}
            />
          </button>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  discounts: state.discounts,
});

ClientDiscounts.propTypes = {
  dispatch: PropTypes.func.isRequired,
  discounts: PropTypes.shape({
    discounts: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  client_discounts: PropTypes.array.isRequired,
  onAssignDiscountToClient: PropTypes.func,
};

export default connect(mapStateToProps)(ClientDiscounts);
